import {
  collection,
  doc,
  query,
  where,
  QueryConstraint,
  getDocs,
  getCountFromServer,
  runTransaction,
  updateDoc,
} from 'firebase/firestore';
import { FiltersType } from 'modules/dashboard/pages/StoriesScreen/types';
import { StorageReference, ref } from 'firebase/storage';

import { firestore, storage } from '../Firebase';
import { Story, STORY_STATUSES } from '../../types/components/Story';

import { deleteFile } from './storageApi';

export type getAllStoriesParams = {
  filters?: FiltersType[];
};

export const getAllStories = async (params: getAllStoriesParams) => {
  const { filters } = params;

  const whereParams: Array<QueryConstraint> = [];

  if (filters?.length) {
    filters.forEach((filter) =>
      whereParams.push(where(filter.field, filter.operator, filter.value)),
    );
  }

  const q = query(collection(firestore, 'stories'), ...whereParams);
  const querySnapshot = await getDocs(q);
  const data: Story[] = [];
  for (let i = 0; i < querySnapshot.docs.length; i++) {
    const doc = querySnapshot.docs[i];

    const {
      title,
      storyAbout,
      storyEnd,
      status,
      publishDate,
      expirationDate,
      imageUrl,
      soundtrackUrl,
      entryFee,
      reward,
      publishedAt,
      createdBy,
      createdAt,
      titleImageUrl,
      publisher,
    } = doc.data();

    const story = {
      id: doc.id,
      title,
      storyAbout,
      storyEnd,
      status,
      publishDate: publishDate ? publishDate.toDate() : null,
      expirationDate: expirationDate ? expirationDate.toDate() : null,
      imageUrl,
      soundtrackUrl,
      entryFee,
      reward,
      publishedAt: publishedAt ? publishedAt.toDate() : null,
      createdBy,
      createdAt: createdAt ? createdAt.toDate() : null,
      chaptersCount: 0,
      titleImageUrl: titleImageUrl,
      publisher,
    };

    //Aggregate chapters count.
    const countRes = await getCountFromServer(
      query(collection(firestore, `stories/${story.id}/chapters`)),
    );
    story.chaptersCount = countRes.data().count ?? 0;

    data.push(story);
  }
  return data;
};

export const deleteStoryById = async (id: string) => {
  const filesRef: StorageReference[] = [];

  const transactionResult = await runTransaction(firestore, async (tx) => {
    //Remove chapters subcollection
    const chaptersQuery = query(collection(firestore, `stories/${id}/chapters`));
    const chaptersQuerySnapshoot = await getDocs(chaptersQuery);
    const chaptersDocs = chaptersQuerySnapshoot?.docs;

    filesRef.push(
      ref(storage, `stories/${id}/image`),
      ref(storage, `stories/${id}/soundtrack`),
      ref(storage, `stories/${id}/titleImage`),
    );

    if (chaptersDocs?.length) {
      for (let i = 0; i < chaptersDocs.length; i++) {
        const chapterId = chaptersDocs[i]?.id;
        if (!chapterId) continue;

        filesRef.push(
          ref(storage, `stories/${id}/chapters/${chapterId}/original`),
          ref(storage, `stories/${id}/chapters/${chapterId}/icon`),
          ref(storage, `stories/${id}/chapters/${chapterId}/titleImage`),
        );

        if (chaptersDocs[i]?.data()?.type === 'video') {
          filesRef.push(
            ref(storage, `stories/${id}/chapters/${chapterId}/video`),
            ref(storage, `stories/${id}/chapters/${chapterId}/iconAnimated`),
            ref(storage, `stories/${id}/chapters/${chapterId}/previewPlaceholder`),
            ref(storage, `stories/${id}/chapters/${chapterId}/previewVideo`),
          );
        }
        if (chaptersDocs[i]?.data()?.type === 'static') {
          filesRef.push(
            ref(storage, `stories/${id}/chapters/${chapterId}/image`),
            ref(storage, `stories/${id}/chapters/${chapterId}/lowQualityImage`),
            ref(storage, `stories/${id}/chapters/${chapterId}/highQualityImage`),
          );
        }

        tx.delete(doc(firestore, `stories/${id}/chapters`, chapterId));
      }
    }

    //Remove main story doc
    return tx.delete(doc(firestore, 'stories', id));
  });

  await Promise.all(filesRef.map((fileRef) => deleteFile(fileRef)));
  return transactionResult;
};

export const publishStoryById = async (id: string) => {
  await updateDoc(doc(firestore, 'stories', id), {
    status: STORY_STATUSES.PUBLISHED,
  });
};

export const unpublishStoryById = async (id: string) => {
  await updateDoc(doc(firestore, 'stories', id), {
    status: STORY_STATUSES.UNPUBLISHED,
  });
};
